import { Axios, AxiosError, AxiosResponse } from 'axios'
import { RemoteConfig } from './remote.config'

export class ChildRefData {
	type: string;
	id: string;
	relationship_first_seen: Date;
	relationship_last_seen: Date;

	constructor(id: string, type: string, relationship_first_seen: Date, relationship_last_seen: Date) {
		this.id = id;
		this.type = type;
		this.relationship_first_seen = relationship_first_seen;
		this.relationship_last_seen = relationship_last_seen;
	}
}

export class RootFQDNData {
	id: string;
	first_seen: Date;
	last_seen: Date;
	fqdn: string;

	children: ChildRefData[];

	constructor(id: string, first_seen: Date, last_seen: Date, fqdn: string, children: ChildRefData[]) {
		this.id = id;
		this.first_seen = first_seen;
		this.last_seen = last_seen;
		this.fqdn = fqdn;
		this.children = children;
	}
}

export class FQDNData {
	id: string;
	first_seen: Date;
	last_seen: Date;
	fqdn: string;

	children: ChildRefData[];

	constructor(id: string, first_seen: Date, last_seen: Date, fqdn: string, children: ChildRefData[]) {
		this.id = id;
		this.first_seen = first_seen;
		this.last_seen = last_seen;
		this.fqdn = fqdn;
		this.children = children;
	}
}

export class IpData {
	id: string;
	first_seen: Date;
	last_seen: Date;
	ip: string;

	children: ChildRefData[];

	constructor(id: string, first_seen: Date, last_seen: Date, ip: string, children: ChildRefData[]) {
		this.id = id;
		this.first_seen = first_seen;
		this.last_seen = last_seen;
		this.ip = ip;
		this.children = children;
	}
}

export class PortData {
	id: string;
	first_seen: Date;
	last_seen: Date;
	port: number;

	children: ChildRefData[];

	constructor(id: string, first_seen: Date, last_seen: Date, port: number, children: ChildRefData[]) {
		this.id = id;
		this.first_seen = first_seen;
		this.last_seen = last_seen;
		this.port = port;
		this.children = children;
	}
}

export class TechnologyData {
	id: string;
	first_seen: Date;
	last_seen: Date;
	technology: string;
	version: string;
	cpe: string;

	children: ChildRefData[];

	constructor(id: string, first_seen: Date, last_seen: Date, technology: string, version: string, cpe: string, children: ChildRefData[]) {
		this.id = id;
		this.first_seen = first_seen;
		this.last_seen = last_seen;
		this.technology = technology;
		this.version = version;
		this.cpe = cpe;
		this.children = children;
	}
}

export class CertificateData {
	id: string;
	first_seen: Date;
	last_seen: Date;
	fingerprint_sha256: string;
	serial: string;
	issuer_dn: string;
	subject_dn: string;
	subject_an: string;
	not_before: Date;
	not_after: Date;

	children: ChildRefData[];

	constructor(id: string, first_seen: Date, last_seen: Date, fingerprint_sha256: string, serial: string, issuer_dn: string, subject_dn: string, subject_an: string, not_before: Date, not_after: Date, children: ChildRefData[]) {
		this.id = id;
		this.first_seen = first_seen;
		this.last_seen = last_seen;
		this.fingerprint_sha256 = fingerprint_sha256;
		this.serial = serial;
		this.issuer_dn = issuer_dn;
		this.subject_dn = subject_dn;
		this.subject_an = subject_an;
		this.not_before = not_before;
		this.not_after = not_after;
		this.children = children;
	}
}

export class WeaknessInstanceData {
	id: string;
	first_seen: Date;
	last_seen: Date;
	extra_info: string;
	remediation: string;
	location: string;
	weakness_info: string;

	weakness: WeaknessInfoData | undefined = undefined;

	children: ChildRefData[];

	constructor(id: string, first_seen: Date, last_seen: Date, extra_info: string, remediation: string, location: string, weakness_info: string, children: ChildRefData[]) {
		this.id = id;
		this.first_seen = first_seen;
		this.last_seen = last_seen;
		this.extra_info = extra_info;
		this.remediation = remediation;
		this.location = location;
		this.weakness_info = weakness_info;
		this.children = children;
	}
}

export class WeaknessInfoData {
	id: string;
	first_seen: Date;

	cwe_id: string | null;
	cvss_metrics: string;
	cvss_score: number | null;
	name: string;
	description: string;
	tags: string[];
	severity: string;

	children: ChildRefData[];

	constructor(id: string, first_seen: Date, cwe_id: string | null, cvss_metrics: string, cvss_score: number | null, name: string, description: string, tags: string[], severity: string, children: ChildRefData[]) {
		this.id = id;
		this.first_seen = first_seen;
		this.cwe_id = cwe_id;
		this.cvss_metrics = cvss_metrics;
		this.cvss_score = cvss_score;
		this.name = name;
		this.description = description;
		this.tags = tags;
		this.severity = severity;
		this.children = children;
	}
}

export class VulnerabilityInstanceData {
	id: string;
	first_seen: Date;
	last_seen: Date;
	extra_info: string;
	remediation: string;
	location: string;
	vulnerability_info: string;

	vulnerability: VulnerabilityInfoData | undefined = undefined;

	children: ChildRefData[];

	constructor(id: string, first_seen: Date, last_seen: Date, extra_info: string, remediation: string, location: string, vulnerability_info: string, children: ChildRefData[]) {
		this.id = id;
		this.first_seen = first_seen;
		this.last_seen = last_seen;
		this.extra_info = extra_info;
		this.remediation = remediation;
		this.location = location;
		this.vulnerability_info = vulnerability_info;
		this.children = children;
	}
}

export class VulnerabilityInfoData {
	id: string;
	first_seen: Date;

	cve_id: string | null;
	cvss_metrics: string;
	cvss_score: number | null;
	name: string;
	description: string;
	tags: string[];
	severity: string;

	children: ChildRefData[];

	constructor(id: string, first_seen: Date, cve_id: string | null, cvss_metrics: string, cvss_score: number | null, name: string, description: string, tags: string[], severity: string, children: ChildRefData[]) {
		this.id = id;
		this.first_seen = first_seen;
		this.cve_id = cve_id;
		this.cvss_metrics = cvss_metrics;
		this.cvss_score = cvss_score;
		this.name = name;
		this.description = description;
		this.tags = tags;
		this.severity = severity;
		this.children = children;
	}
}

export type RootFQDNCallback = (data?: RootFQDNData, resp?: AxiosResponse, err?: AxiosError) => void;

export const getRootFQDN = (id: string, callback: RootFQDNCallback) => {
	const client = new Axios();
	const url = new URL(`/api/v0/scan_database/root_fqdn/${id}`, RemoteConfig.url);
	const result = client.get(url.toString(), {withCredentials:true}).then((resp) => {
		try {
			let data: RootFQDNData = JSON.parse(resp.data)
			callback(data, resp)
		} catch {
			callback(undefined, resp)
		}
	})
}

export type FQDNCallback = (data?: FQDNData, resp?: AxiosResponse, err?: AxiosError) => void;

export const getFQDN = (id: string, callback: FQDNCallback) => {
	const client = new Axios();
	const url = new URL(`/api/v0/scan_database/fqdn/${id}`, RemoteConfig.url);
	const result = client.get(url.toString(), {withCredentials:true}).then((resp) => {
		try {
			let data: FQDNData = JSON.parse(resp.data)
			callback(data, resp)
		} catch {
			callback(undefined, resp)
		}
	})
}

export type IpCallback = (data?: IpData, resp?: AxiosResponse, err?: AxiosError) => void;

export const getIp = (id: string, callback: IpCallback) => {
	const client = new Axios();
	const url = new URL(`/api/v0/scan_database/ip/${id}`, RemoteConfig.url);
	const result = client.get(url.toString(), {withCredentials:true}).then((resp) => {
		try {
			let data: IpData = JSON.parse(resp.data)
			callback(data, resp)
		} catch {
			callback(undefined, resp)
		}
	})
}

export type PortCallback = (data?: PortData, resp?: AxiosResponse, err?: AxiosError) => void;

export const getPort = (id: string, callback: PortCallback) => {
	const client = new Axios();
	const url = new URL(`/api/v0/scan_database/port/${id}`, RemoteConfig.url);
	const result = client.get(url.toString(), {withCredentials:true}).then((resp) => {
		try {
			let data: PortData = JSON.parse(resp.data)
			callback(data, resp)
		} catch {
			callback(undefined, resp)
		}
	})
}

export type CertificateCallback = (data?: CertificateData, resp?: AxiosResponse, err?: AxiosError) => void;

export const getCertificate = (id: string, callback: CertificateCallback) => {
	const client = new Axios();
	const url = new URL(`/api/v0/scan_database/certificate/${id}`, RemoteConfig.url);
	const result = client.get(url.toString(), {withCredentials:true}).then((resp) => {
		try {
			let data: CertificateData = JSON.parse(resp.data)
			callback(data, resp)
		} catch {
			callback(undefined, resp)
		}
	})
}

export type TechnologyCallback = (data?: TechnologyData, resp?: AxiosResponse, err?: AxiosError) => void;

export const getTechnology = (id: string, callback: TechnologyCallback) => {
	const client = new Axios();
	const url = new URL(`/api/v0/scan_database/technology/${id}`, RemoteConfig.url);
	const result = client.get(url.toString(), {withCredentials:true}).then((resp) => {
		try {
			let data: TechnologyData = JSON.parse(resp.data)
			callback(data, resp)
		} catch {
			callback(undefined, resp)
		}
	})
}

export type WeaknessInstanceCallback = (data?: WeaknessInstanceData, resp?: AxiosResponse, err?: AxiosError) => void;

export const getWeaknessInstance = (id: string, callback: WeaknessInstanceCallback) => {
	const client = new Axios();
	const url = new URL(`/api/v0/scan_database/weakness/${id}`, RemoteConfig.url);
	const result = client.get(url.toString(), {withCredentials:true}).then((resp) => {
		try {
			let data: WeaknessInstanceData = JSON.parse(resp.data)
			callback(data, resp)
		} catch {
			callback(undefined, resp)
		}
	})
}

export type VulnerabilityInstanceCallback = (data?: VulnerabilityInstanceData, resp?: AxiosResponse, err?: AxiosError) => void;

export const getVulnerabilityInstance = (id: string, callback: VulnerabilityInstanceCallback) => {
	const client = new Axios();
	const url = new URL(`/api/v0/scan_database/vulnerability/${id}`, RemoteConfig.url);
	const result = client.get(url.toString(), {withCredentials:true}).then((resp) => {
		try {
			let data: VulnerabilityInstanceData = JSON.parse(resp.data)
			callback(data, resp)
		} catch {
			callback(undefined, resp)
		}
	})
}
